import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Users, Trophy } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useCountdown } from '@/hooks/useCountdown';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { useToastStore } from '@/store/toast';
import { useAuthStore } from '@/store/auth';
import { formatDate, formatNumber } from '@/lib/format';
import { contestsApi, leaderboardApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import type {
  ContestSummaryResponse,
  LeaderboardEntryResponse,
} from '@/api/types';

export function Contests() {
  const { t } = useTranslation();
  const { lang } = useLocalized();

  useSeo({
    title: 'SQL musobaqalari — SQLMaster.uz',
    description:
      "SQL musobaqalarida qatnashing, masalalarni tezlik bilan yeching va reytingda o'z o'rningizni egallang.",
    path: '/contests',
  });

  const [upcoming, setUpcoming] = useState<ContestSummaryResponse[]>([]);
  const [live, setLive] = useState<ContestSummaryResponse[]>([]);
  const [past, setPast] = useState<ContestSummaryResponse[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      contestsApi.list({ state: 'UPCOMING', page: 0, size: 20, lang }),
      contestsApi.list({ state: 'LIVE', page: 0, size: 20, lang }),
      contestsApi.list({ state: 'FINISHED', page: 0, size: 20, lang }),
      leaderboardApi.list(0, 50),
    ])
      .then(([upRes, liveRes, pastRes, lbRes]) => {
        setUpcoming(upRes.data.content);
        setLive(liveRes.data.content);
        setPast(pastRes.data.content);
        setLeaderboard(lbRes.data.content);
      })
      .catch((err) => setError(getApiErrorMessage(err, t('common.retry'))))
      .finally(() => setLoading(false));
  }, [lang, t]);

  useEffect(() => {
    load();
  }, [load]);

  const active = [...live, ...upcoming];

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold sm:text-3xl">{t('contests.title')}</h1>
        <p className="mt-1 text-content-secondary">{t('contests.subtitle')}</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-lg bg-surface" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-lg bg-surface" />
        </div>
      ) : error ? (
        <Card className="p-6 text-center">
          <p className="text-content-secondary">{error}</p>
          <Button className="mt-4" size="sm" onClick={load}>
            {t('common.retry')}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <section>
              <h2 className="mb-3 text-lg font-semibold">{t('contests.upcoming')}</h2>
              {active.length === 0 ? (
                <Card className="p-6 text-center text-content-secondary">
                  {t('contests.emptyUpcoming')}
                </Card>
              ) : (
                <div className="space-y-4">
                  {active.map((c) => (
                    <ContestCard key={c.id} contest={c} onChanged={load} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-3 text-lg font-semibold">{t('contests.past')}</h2>
              {past.length === 0 ? (
                <Card className="p-4 text-center text-sm text-content-muted">
                  {t('contests.emptyPast')}
                </Card>
              ) : (
                <div className="space-y-3">
                  {past.map((c) => (
                    <Link key={c.id} to={`/contests/${c.slug}`}>
                      <Card className="flex flex-wrap items-center justify-between gap-3 p-4 transition hover:border-brand-500/40">
                        <div>
                          <div className="font-medium">{c.title}</div>
                          <div className="mt-0.5 text-sm text-content-muted">
                            {formatDate(c.startsAt, lang)}
                            {c.participantCount > 0 && (
                              <> · {t('contests.participants', { count: c.participantCount })}</>
                            )}
                          </div>
                        </div>
                        <span className="rounded bg-surface px-2.5 py-1 text-xs font-medium text-content-secondary">
                          {t('contests.finished')}
                        </span>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="lg:col-span-1">
            <Card className="p-5">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <Trophy className="h-5 w-5 text-[var(--medium)]" />
                {t('contests.leaderboard')}
              </h2>
              {leaderboard.length === 0 ? (
                <p className="text-sm text-content-muted">{t('contests.emptyLeaderboard')}</p>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-3 border-b border-line pb-2 text-xs uppercase tracking-wide text-content-muted">
                    <span className="w-6 text-center">{t('contests.colRank')}</span>
                    <span className="flex-1">{t('contests.colUser')}</span>
                    <span className="w-14 text-right">{t('contests.colSolved')}</span>
                  </div>
                  {leaderboard.map((e) => (
                    <div
                      key={`${e.rank}-${e.username}`}
                      className="flex items-center gap-3 rounded-DEFAULT px-1 py-2 text-sm hover:bg-surface"
                    >
                      <span
                        className={
                          e.rank <= 3
                            ? 'w-6 text-center font-bold text-[var(--medium)]'
                            : 'w-6 text-center text-content-muted'
                        }
                      >
                        {e.rank}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                          {e.displayName || e.username}
                        </div>
                        <div className="truncate text-xs text-content-muted">@{e.username}</div>
                      </div>
                      <span className="tnum w-14 text-right font-mono text-content-secondary">
                        {formatNumber(e.solvedCount, lang)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function ContestCard({
  contest,
  onChanged,
}: {
  contest: ContestSummaryResponse;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);

  const countdownTarget = contest.state === 'LIVE' ? contest.endsAt : contest.startsAt;
  const cd = useCountdown(countdownTarget);

  useEffect(() => {
    if (cd.done) {
      onChanged();
    }
    // faqat tugaganda qayta so'rash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cd.done]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRegistered(false);
      return;
    }
    let cancelled = false;
    contestsApi
      .me(contest.slug)
      .then((res) => {
        if (!cancelled) setRegistered(res.data.registered);
      })
      .catch(() => {
        if (!cancelled) setRegistered(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contest.slug, isAuthenticated]);

  const handleRegister = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/contests`);
      return;
    }
    setBusy(true);
    try {
      await contestsApi.register(contest.slug);
      setRegistered(true);
      push(t('toast.contestRegistered'), 'success');
      onChanged();
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setBusy(false);
    }
  };

  const isLive = contest.state === 'LIVE';

  return (
    <Card hover className="p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/contests/${contest.slug}`} className="text-lg font-semibold hover:text-brand-600">
              {contest.title}
            </Link>
            {isLive && (
              <span className="rounded bg-[var(--error)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--error)]">
                {t('contests.live')}
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-content-muted">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(contest.startsAt, lang)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {t('contests.duration', { min: contest.durationMin })}
            </span>
            {contest.problemCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Trophy className="h-4 w-4" />
                {t('contests.problemsN', { count: contest.problemCount })}
              </span>
            )}
            {contest.participantCount > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {t('contests.participants', { count: contest.participantCount })}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 sm:items-end">
          {!cd.done && (
            <div className="flex gap-2 text-center">
              {[
                { v: cd.days, u: t('contests.days') },
                { v: cd.hours, u: t('contests.hours') },
                { v: cd.minutes, u: t('contests.minutes') },
              ].map((seg, i) => (
                <div key={i} className="rounded-DEFAULT bg-surface px-2.5 py-1.5">
                  <div className="tnum text-lg font-bold leading-none">
                    {String(seg.v).padStart(2, '0')}
                  </div>
                  <div className="mt-0.5 text-[10px] uppercase text-content-muted">{seg.u}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate(`/contests/${contest.slug}`)}>
              {t('contests.enter')}
            </Button>
            {(contest.state === 'UPCOMING' || contest.state === 'LIVE') && (
              <Button
                variant={registered ? 'secondary' : 'primary'}
                size="sm"
                disabled={registered || busy}
                loading={busy}
                onClick={handleRegister}
              >
                {registered ? t('contests.registered') : t('contests.register')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
